import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { wizardDistributionPartnerKeys, wizardDistributionPartnersApi } from '@client/api/waterlily';
import type { API } from '@/api/types';

type WizardDistributionPartnerInput = API.WizardDistributionPartnerInput;
type WizardDistributionPartnerPatch = API.WizardDistributionPartnerPatch;

export function useWizardDistributionPartners() {
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: wizardDistributionPartnerKeys.list(),
    queryFn: () => wizardDistributionPartnersApi.list(),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: wizardDistributionPartnerKeys.list() });

  const createMutation = useMutation({
    mutationFn: (input: WizardDistributionPartnerInput) =>
      wizardDistributionPartnersApi.create(input),
    onSuccess: () => {
      toast.success('Partner added');
      invalidate();
    },
    onError: (error: unknown) => {
      toast.error('Failed to add partner', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: WizardDistributionPartnerPatch }) =>
      wizardDistributionPartnersApi.update(id, patch),
    onSuccess: () => {
      toast.success('Partner updated');
      invalidate();
    },
    onError: (error: unknown) => {
      toast.error('Failed to update partner', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => wizardDistributionPartnersApi.delete(id),
    onSuccess: () => {
      toast.success('Partner deleted');
      invalidate();
    },
    onError: (error: unknown) => {
      toast.error('Failed to delete partner', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  return {
    partners: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    createPartner: createMutation.mutate,
    isCreating: createMutation.isPending,
    updatePartner: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    deletePartner: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
}
